import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChartOutlined,
  TeamOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';

const Analytics = () => {
  const { trainingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const [labAnalytics, setLabAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState(30); // days

  useEffect(() => {
    if (trainingId) {
      fetchTrainingAnalytics();
    }
  }, [trainingId]);

  useEffect(() => {
    if (selectedLab) {
      fetchLabAnalytics(selectedLab);
    }
  }, [selectedLab]);

  const fetchTrainingAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/analytics/training/${trainingId}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching training analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLabAnalytics = async (labId) => {
    try {
      const response = await api.get(`/api/analytics/lab/${labId}`);
      setLabAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching lab analytics:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No analytics data available</div>
      </div>
    );
  }

  // Chart colors
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Format score distribution data for pie chart
  const scoreDistributionData = labAnalytics?.score_distribution?.map((range) => ({
    name: range.range,
    value: range.count,
  })) || [];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Training Analytics
        </h1>
        <p className="text-gray-600">{analyticsData.training_name}</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Students"
          value={analyticsData.total_students}
          icon={TeamOutlined}
          color="blue"
        />
        <MetricCard
          title="Completion Rate"
          value={`${analyticsData.completion_rate}%`}
          icon={CheckCircleOutlined}
          color="green"
        />
        <MetricCard
          title="Average Score"
          value={analyticsData.average_score}
          icon={TrophyOutlined}
          color="purple"
        />
        <MetricCard
          title="Total Sessions"
          value={analyticsData.total_sessions}
          icon={BarChartOutlined}
          color="orange"
        />
      </div>

      {/* Daily Trends Chart */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Session Trends (Last 30 Days)
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analyticsData.daily_trends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="sessions"
              stroke="#3B82F6"
              strokeWidth={2}
              name="Sessions"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Lab Breakdown */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Lab Performance Breakdown
        </h2>
        <div className="space-y-4">
          {analyticsData.lab_breakdown?.map((lab) => (
            <div
              key={lab.lab_id}
              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                selectedLab === lab.lab_id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setSelectedLab(lab.lab_id)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-gray-900">{lab.lab_name}</h3>
                <span className="text-sm text-gray-500">
                  Lab {lab.sequence_order}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Sessions</p>
                  <p className="font-semibold text-gray-900">
                    {lab.total_sessions}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Completion</p>
                  <p className="font-semibold text-gray-900">
                    {lab.completion_rate.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Avg Score</p>
                  <p className="font-semibold text-gray-900">
                    {lab.average_score}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Avg Duration</p>
                  <p className="font-semibold text-gray-900">
                    {lab.average_duration_minutes.toFixed(0)} min
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Lab Analytics */}
      {labAnalytics && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Detailed Lab Analytics: {labAnalytics.lab_name}
            </h2>

            {/* Lab Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600 mb-1">Active Sessions</p>
                <p className="text-2xl font-bold text-blue-900">
                  {labAnalytics.active_sessions}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600 mb-1">Completed</p>
                <p className="text-2xl font-bold text-green-900">
                  {labAnalytics.completed_sessions}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-600 mb-1">Avg Duration</p>
                <p className="text-2xl font-bold text-purple-900">
                  {labAnalytics.average_duration_minutes.toFixed(0)} min
                </p>
              </div>
            </div>

            {/* Score Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-4">
                  Score Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={scoreDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {scoreDistributionData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Criteria Performance */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-4">
                  Criteria Performance
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={labAnalytics.criteria_performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="criteria_type" angle={-45} textAnchor="end" height={100} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey="average_score" fill="#3B82F6" name="Avg Score" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Criteria Details Table */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">
              Evaluation Criteria Details
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criteria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weight
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {labAnalytics.criteria_performance?.map((criteria, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {criteria.description}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {criteria.criteria_type.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`font-semibold ${
                            criteria.average_score >= 80
                              ? 'text-green-600'
                              : criteria.average_score >= 60
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {criteria.average_score}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {(criteria.weight * 100).toFixed(0)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
